// src/components/compartidos/Header.js
import React, { useState, useRef, useEffect } from 'react';
import './Header.css';
import logoWisby from '../../assets/logo.png';
import ProfileMenu from '../cuenta/ProfileMenu'; 

// Componente `ProfileIconSVG` que renderiza esta parte de la interfaz.
const ProfileIconSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Componente `Header` que renderiza esta parte de la interfaz.
export default function Header({
  currentUser,
  userRole,
  isAdmin,
  onSetActiveSection,
  onToggleMenu,
  currentLanguage,
  translations,
  onLogout, 
  onSetProfileTab,
  isLanding = false,
  notifications,
  t = {}
}) {
  
  // Estado local `isProfileMenuOpen` y su actualizador, usados para reflejar cambios en la interfaz.
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  // Estado local `isServicesMenuOpen` y su actualizador, usados para reflejar cambios en la interfaz.
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState('');
  const notaryRoles = ['notario', 'admin_notaria', 'abogado_notaria'];
  const portalRole = notaryRoles.includes(userRole) ? userRole : notaryRoles.find(role => availableRoles.includes(role));
  useEffect(() => {
    if (!currentUser) { setAvailableRoles([]); return; }
    if (!isProfileMenuOpen) return;
    const controller = new AbortController();
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/roles/me`, { headers: { 'x-auth-token': token }, signal: controller.signal })
      .then(async response => { if (!response.ok) return null; return response.json(); })
      .then(data => { if (!controller.signal.aborted && data) setAvailableRoles(data.roles || []); })
      .catch(() => {});
    return () => controller.abort();
  }, [currentUser, userRole, isProfileMenuOpen]);
  const openNotaryPortal = async () => {
    if (!portalRole || portalBusy) return;
    if (portalRole === userRole) { setIsProfileMenuOpen(false); onSetActiveSection('notario-dashboard'); return; }
    setPortalBusy(true); setPortalError('');
    try {
      const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/roles/active`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'x-auth-token': storage.getItem('token') }, body: JSON.stringify({ role: portalRole }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.msg || 'No se pudo abrir el portal de notaría.');
      storage.setItem('userRole', result.role);
      window.location.assign('/');
    } catch (error) { setPortalError(error.message); setPortalBusy(false); }
  };
  // Referencia mutable `menuRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const menuRef = useRef(null);
  // Referencia mutable `servicesRef` que conserva datos entre renderizados sin provocar uno nuevo.
  const servicesRef = useRef(null);

  useEffect(() => {
    // Función `handleClickOutside` que gestiona la interacción o el evento correspondiente.
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuRef]);

  // Función `handleNavigateFromMenu` que gestiona la interacción o el evento correspondiente.
  const handleNavigateFromMenu = (section, tab) => {
    onSetActiveSection(section, tab);
    setIsProfileMenuOpen(false);
  };

  // Función `handleLogoutFromMenu` que gestiona la interacción o el evento correspondiente.
  const handleLogoutFromMenu = () => {
    if (onLogout) onLogout(); 
    setIsProfileMenuOpen(false);
  };

  // Función `handleServiceSelect` que gestiona la interacción o el evento correspondiente.
  const handleServiceSelect = (section) => {
    if (currentUser) {
      onSetActiveSection(section);
    } else {
      onSetActiveSection('registrate');
    }
    setIsServicesMenuOpen(false);
  };

  return (
    <header className={`header-wisby${isLanding ? ' header-wisby--landing' : ''}`}>
      <div className="header-content">
        <img 
          src={logoWisby} 
          alt="Wisby Logo" 
          className="logo-wisby" 
          onClick={() => onSetActiveSection('inicio')} 
        />
        
        <nav className="nav-links-wisby">
          <button onClick={() => onSetActiveSection('nosotros')} className="nav-button-wisby">
            {t.nosotros || "Nosotros"} 
          </button>
          
          <div className="services-dropdown" ref={servicesRef}>
            <button 
              onClick={() => setIsServicesMenuOpen(!isServicesMenuOpen)} 
              className="nav-button-wisby services-trigger"
            >
              {t.servicios || "Servicios"}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, transition: 'transform 0.2s', transform: isServicesMenuOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {isServicesMenuOpen && (
              <div className="services-dropdown-menu">
                <button onClick={() => handleServiceSelect('crear-accion')} className="services-dropdown-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                  </svg>
                  <div>
                    <span className="services-item-title">Acciones</span>
                    <span className="services-item-desc">Títulos accionarios corporativos</span>
                  </div>
                </button>
                <button onClick={() => handleServiceSelect('crear-poder')} className="services-dropdown-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <path d="M12 18v-6"></path>
                    <path d="M9 15l3 3 3-3"></path>
                  </svg>
                  <div>
                    <span className="services-item-title">Poderes</span>
                    <span className="services-item-desc">Otorgamiento de poderes legales</span>
                  </div>
                </button>
              </div>
            )}
          </div>
          
          {notifications}

          {currentUser ? (
            <div className="profile-container" ref={menuRef} style={{position: 'relative'}}>
              <div 
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} 
                className={`profile-icon ${isAdmin ? 'is-admin' : ''}`}
                role="button"
                tabIndex={0}
                aria-label="Menú de cuenta"
                aria-expanded={isProfileMenuOpen}
                onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setIsProfileMenuOpen(value => !value); } }}
                title={isAdmin ? "Mi Perfil (Admin)" : "Mi Perfil"}
              >
                <ProfileIconSVG />
                {/* Indicador visual de admin */}
                {isAdmin && <span className="admin-indicator"></span>}
              </div>

              {/* Renderizamos el menú si el estado es true */}
              {isProfileMenuOpen && (
                <ProfileMenu 
                  userName={currentUser} 
                  onNavigate={handleNavigateFromMenu}
                  onLogout={handleLogoutFromMenu}
                  isAdmin={isAdmin}
                  isNotario={Boolean(portalRole)}
                  onNotarioPortal={openNotaryPortal}
                  notarioLoading={portalBusy}
                  portalError={portalError}
                  t={t}
                />
              )}
            </div>
          ) : (
            <button 
              onClick={() => {
                onSetActiveSection('auth');
                window.dispatchEvent(new Event('wisby:open-login'));
              }}
              className="nav-button-wisby action-button"
            >
              {t.iniciarSesion || "Iniciar Sesión"}
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
